import { type Registry as Services, service } from '@ember/service';
import type { RequestData } from '@ember-data/adapter/rest';
import RESTAdapter from '@ember-data/adapter/rest';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type { HTTPMethod } from '@warp-drive/core-types/request';
import { getHostDomain } from 'frontend-payments/utils/domain-checks';
import { underscore } from 'frontend-utils/utils/convert-case';
import { pluralize } from 'frontend-utils/utils/ember-data';
import Cookies from 'js-cookie';
import type RSVP from 'rsvp';

function extendOptions(
  type: HTTPMethod,
  options: Record<string, unknown>,
): Record<string, unknown> {
  // This endpoint requires authentication which is done via the sessionId present in the cookie
  options['credentials'] = 'include';
  options['crossDomain'] = true;
  options['xhrFields'] = { withCredentials: true };

  const csrftoken = Cookies.get('csrftoken');

  if (isWriteOperation(type) && csrftoken) {
    options['headers'] = {
      'X-CSRFToken': csrftoken,
    };
  }

  return options;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function isWriteOperation(method: string) {
  return !/^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
}

export default class ApplicationAdapter extends RESTAdapter {
  @service declare session: Services['session'];

  host = getHostDomain();
  namespace = 'api/v4';

  // @ts-expect-error: Property ajaxOptions in type 'ApplicationAdapter' is not assignable to the same property in base type 'BaseRESTAdapter'
  ajaxOptions(
    url: string,
    type: HTTPMethod,
    options: Record<string, unknown>,
  ): object {
    const newOptions = extendOptions(type, options);

    return super.ajaxOptions(url, type, newOptions);
  }

  // @ts-expect-error: Incorrect type
  buildURL(modelName: string, id: string, snapshot: Snapshot<unknown>): string {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const _buildURL = (...args: Parameters<RESTAdapter['buildURL']>) => {
      let url = super.buildURL(...args);

      if (url.charAt(url.length - 1) !== '/') {
        url += '/';
      }

      return url;
    };

    // Allow overriding the namespace per-request via adapterOptions
    if (
      snapshot &&
      snapshot.adapterOptions &&
      snapshot.adapterOptions['namespace']
    ) {
      const originalNamespace = this.namespace;
      this.namespace = snapshot.adapterOptions['namespace'] as string;

      const url = _buildURL(modelName, id, snapshot);
      this.namespace = originalNamespace; // Restore original namespace

      return url;
    }

    return _buildURL(modelName, id, snapshot);
  }

  // Handle query/queryRecord with dynamic namespace from adapterOptions
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleQuery(
    store: Store,
    type: ModelSchema,
    query: Record<string, unknown> & {
      adapterOptions?: {
        namespace?: string;
      };
    },
  ) {
    const originalNamespace = this.namespace;

    if (query.adapterOptions && query.adapterOptions.namespace) {
      this.namespace = query.adapterOptions.namespace;
      // Remove adapterOptions from query before passing to super
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { adapterOptions, ...cleanQuery } = query;
      query = cleanQuery;
    }

    const result = super.query(store, type, query);

    // Restore original namespace
    this.namespace = originalNamespace;

    return result;
  }

  // Handle authentication errors by invalidating session and redirecting to login
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleResponse(
    status: number,
    headers: Record<string, string>,
    payload: {
      error_message?: string;
      error_param?: string;
      errors?: object[];
    },
    requestData: RequestData,
  ) {
    if (status === 401) {
      // Session is unauthorized - invalidate the session and redirect to login
      void this.session.invalidate();
      return super.handleResponse(status, headers, payload, requestData);
    }

    return super.handleResponse(status, headers, payload, requestData);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  pathForType(modelName: string) {
    return pluralize(modelName);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  query(store: Store, type: ModelSchema, query: Record<string, unknown>) {
    return this.handleQuery(store, type, query);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  queryRecord(store: Store, type: ModelSchema, query: Record<string, unknown>) {
    return this.handleQuery(store, type, query);
  }

  updateRecord(
    store: Store,
    schema: ModelSchema,
    snapshot: Snapshot,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): RSVP.Promise<any> {
    const { usePatch } = snapshot.adapterOptions || {};

    if (!usePatch) {
      // @ts-expect-error Property 'new' is missing in type 'Promise<AdapterPayload>' but required in type 'Promise<any>'
      return super.updateRecord(store, schema, snapshot);
    }

    const url = this.buildURL(
      schema.modelName,
      snapshot.id,
      snapshot,
      // @ts-expect-error Expected 3 arguments, but got 4
      'updateRecord',
    );

    const serializedSnapshot = snapshot.serialize({ includeId: true });
    const changedAttrs = snapshot.changedAttributes();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const payloadKey = store
      .serializerFor(snapshot.modelName)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .payloadKeyFromModelName(snapshot.modelName);

    const data = {
      [payloadKey]: {},
    };

    Object.keys(changedAttrs).forEach((key: string) => {
      const decamelizedKey = underscore(key);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      data[payloadKey][decamelizedKey] = serializedSnapshot[decamelizedKey];
    });

    // @ts-expect-error: Incorrect type
    return this.ajax(url, 'PATCH', { data });
  }
}
